import React, {useCallback, useEffect, useRef, useState} from 'react'
import StyleSheet from '../utils/helpers/styleSheet'
import RenderImg from './editArea/Img'
import {v4 as uuidv4} from 'uuid'
import {origo} from '../utils/helpers/origo'
import {useAppDispatch, useAppSelector} from '../stores/configureStore'
import {
  addImage,
  flipSide,
  moveImage,
  resetEditorStore,
  setAttribute,
  setInert,
  setInteractedImage,
  setPrintArea,
  setRotation,
  setScale,
  setZoom,
} from '../stores/editor/editor.reducer'
import {clamp} from '../utils/helpers/clamp'
import {Config} from '../constants/Config'
import {ImgUrl} from '../models/editor/imgUrl'
import services from '../utils/services'
import {
  resetToolboxStore,
  selectToolboxTabPanel,
} from '../stores/toolbox/toolbox.reducer'
import {ImgText} from '../models/editor/imgText'
import {ImgMasked} from '../models/editor/imgMasked'
import {SaveProductRequest} from '../models/dto/saveProduct.request'
import {useParams} from 'react-router-dom'
import {Img} from '../models/editor/img'
import ImgTools from './editArea/ImgTools'
import InsideImgTools from './editArea/InsideImgTools'
import {createPrintArea} from '../utils/helpers/createPrintArea'

const styles: StyleSheet = {
  editor: {
    width: '100%',
    height: 500,
    boxSizing: 'border-box',
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    userSelect: 'none',
  },
  editorColumn: {
    flexBasis: '50%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
  },
  editArea: {
    width: '100%',
    height: '100%',
  },
  toolboxColumn: {
    flexBasis: '50%',
    display: 'flex',
    flexDirection: 'column',
    margin: 36,
  },
}

export default function FrontendEditor() {
  const dispatch = useAppDispatch()
  const {images, zoom, interactedImage, printArea, selectedImageId} =
    useAppSelector(store => store.editor)
  const editorRef = useRef<HTMLDivElement | null>(null)
  const zoomRef = useRef<HTMLDivElement | null>(null)
  let {productId} = useParams() as any
  const [id, setId] = useState<number | undefined>(undefined)
  const [dragging, setDragging] = useState(false)
  const [inactiveImages, setInactiveImages] = useState<Img[] | null>(null)
  const [inactivePrintArea, setInactivePrintArea] = useState<ImgText | null>(
    null,
  )
  const [activeSide, setActiveSide] = useState<'front' | 'back'>('front')
  const twoSided = inactiveImages !== null && inactivePrintArea !== null

  const onWheel: React.UIEventHandler<HTMLDivElement> = e => {
    if (editorRef.current) {
      const rect = editorRef.current.getBoundingClientRect()
      const newZoomLevel = clamp(
        //@ts-ignore
        e.deltaY > 0 ? zoom.level / 1.1 : zoom.level * 1.1,
        Config.minZoom,
        Config.maxZoom,
      )
      //@ts-ignore
      const imageX = e.clientX - rect.left
      //@ts-ignore
      const imageY = e.clientY - rect.top
      const width = rect.width
      const height = rect.height
      const relativeX = imageX - width / 2
      const relativeY = imageY - height / 2
      const newTranslateX =
        ((zoom.translate.x - relativeX) / zoom.level) * newZoomLevel + relativeX
      const newTranslateY =
        ((zoom.translate.y - relativeY) / zoom.level) * newZoomLevel + relativeY

      dispatch(
        setZoom({
          level: newZoomLevel,
          translate: {x: newTranslateX, y: newTranslateY},
        }),
      )
    }
  }

  useEffect(() => {
    if (editorRef.current) {
      const editorRefCurrent = editorRef.current
      const cancelWheel = (event: WheelEvent) => event.preventDefault()

      editorRefCurrent.addEventListener('wheel', cancelWheel, {passive: false})
      return () => {
        editorRefCurrent.removeEventListener('wheel', cancelWheel)
      }
    }
  }, [editorRef])

  const tryGetPic = useCallback(
    async (id: number) => {
      const response = await services.getProduct(id)
      const product = response.data
      if (product.colors.length > 0) {
        const productImage: ImgUrl = {
          id: uuidv4(),
          type: 'url',
          url:
            product.colors[0].pages[0].maskUrl ??
            product.colors[0].pages[0].imageUrl,
          brightness: 0.9,
          filters: {inverse: false, monochrome: false, sepia: false},
          inert: false,
          locked: false,
          mirroredX: false,
          mirroredY: false,
          position: {
            x: 1 - product.sides.front.position.x / product.sides.front.scale,
            y: 1 - product.sides.front.position.y / product.sides.front.scale,
          },
          rotation: 0,
          scale: 1 / product.sides.front.scale,
        }
        let printWidthMm = product.pricing.szivoldalWidthMm
        let printHeightMm = product.pricing.szivoldalHeightMm
        if (product.pricing.editableArea === 'a4') {
          printWidthMm = product.pricing.a4WidthMm
          printHeightMm = product.pricing.a4HeightMm
        } else if (product.pricing.editableArea === 'a3') {
          printWidthMm = product.pricing.a3WidthMm
          printHeightMm = product.pricing.a3HeightMm
        }
        const printArea: ImgText = createPrintArea(printWidthMm, printHeightMm)

        dispatch(resetEditorStore())
        dispatch(resetToolboxStore())
        dispatch(setZoom({level: 0.2, translate: origo}))
        dispatch(addImage({image: productImage}))
        dispatch(setPrintArea({printArea: printArea}))
        if (product.twoSided) {
          const backProductImage: ImgUrl = {
            ...productImage,
            id: uuidv4(),
            url:
              product.colors[0].pages[1].maskUrl ??
              product.colors[0].pages[1].imageUrl,
            position: {
              x: 1 - product.sides.back.position.x / product.sides.back.scale,
              y: 1 - product.sides.back.position.y / product.sides.back.scale,
            },
            scale: 1 / product.sides.back.scale,
          }
          setInactiveImages(() => [backProductImage])
          let printWidthMm = product.pricing.szivoldalWidthMm
          let printHeightMm = product.pricing.szivoldalHeightMm
          if (product.pricing.editableAreaBack === 'a4') {
            printWidthMm = product.pricing.a4WidthMm
            printHeightMm = product.pricing.a4HeightMm
          } else if (product.pricing.editableAreaBack === 'a3') {
            printWidthMm = product.pricing.a3WidthMm
            printHeightMm = product.pricing.a3HeightMm
          }
          const backPrintArea: ImgText = {
            ...printArea,
            id: uuidv4(),
            type: 'text',
            text: `<svg width="${printWidthMm}mm" height="${printHeightMm}mm" viewBox="0 0 ${printWidthMm}mm ${printHeightMm}mm">
                 <rect width="${printWidthMm}mm" height="${printHeightMm}mm" style="fill:transparent;stroke-width:4;stroke:rgb(0,0,0)" stroke-dasharray="5,5" vector-effect: non-scaling-stroke; />
                 </svg>`,
          }
          setInactivePrintArea(() => backPrintArea)
        }
      }
    },
    [dispatch],
  )

  const reloadEditor = useCallback(async () => {
    if (id) {
      dispatch(resetEditorStore())
      dispatch(setZoom({level: 0.1, translate: origo}))
      dispatch(selectToolboxTabPanel({tabPanel: 'AddCaption'}))
      tryGetPic(id)
    }
  }, [dispatch, tryGetPic, id])

  useEffect(() => {
    reloadEditor()
  }, [reloadEditor, id])

  useEffect(() => {
    const id = Number.parseInt(productId)
    if (id) {
      setId(() => id)
    } else {
      setId(() => undefined)
    }
  }, [productId])

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e.button === 1) {
      e.preventDefault()
      setDragging(() => true)
      dispatch(setInert({inert: true}))
      //Todo unselect image
    }
  }

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault()
    if (interactedImage) {
      let image = images.find(image => image.id === interactedImage.id)
      let maskedImg = image as ImgMasked
      if (
        interactedImage.innerImageIndex !== undefined &&
        maskedImg.inside !== undefined
      ) {
        //Inside image
        switch (interactedImage.interaction) {
          case 'dragging':
            dispatch(
              dispatch(
                setAttribute({
                  id: interactedImage.id,
                  data: {
                    inside: maskedImg.inside.map((item, i) =>
                      i === interactedImage.innerImageIndex && item
                        ? {
                            ...item,
                            position: {
                              x:
                                e.nativeEvent.movementX /
                                window.devicePixelRatio /
                                zoom.level,
                              y:
                                e.nativeEvent.movementY /
                                window.devicePixelRatio /
                                zoom.level,
                            },
                          }
                        : item,
                    ),
                  },
                }),
              ),
            )
            break
          case 'scaling':
            dispatch(
              setAttribute({
                id: interactedImage.id,
                data: {
                  inside: maskedImg.inside.map((item, i) =>
                    i === interactedImage.innerImageIndex && item
                      ? {
                          ...item,
                          scale: clamp(
                            (item.scale as number) -
                              e.nativeEvent.movementY / 100,
                            0.1,
                            5,
                          ),
                        }
                      : item,
                  ),
                },
              }),
            )
            break
          default:
            break
        }
      } else {
        //Main image
        switch (interactedImage.interaction) {
          case 'dragging':
            dispatch(
              moveImage({
                offset: {
                  x:
                    e.nativeEvent.movementX /
                    window.devicePixelRatio /
                    zoom.level,
                  y:
                    e.nativeEvent.movementY /
                    window.devicePixelRatio /
                    zoom.level,
                },
              }),
            )
            break
          case 'scaling':
            dispatch(
              setScale({
                id: interactedImage.id,
                scale: clamp(
                  (image?.scale as number) - e.nativeEvent.movementY / 500,
                  Config.minZoom,
                  Config.maxZoom,
                ),
              }),
            )
            break
          case 'rotating':
            dispatch(
              setRotation({
                id: interactedImage.id,
                rotation: (image?.rotation as number) - e.nativeEvent.movementY,
              }),
            )
            break
          default:
            break
        }
      }
    }
    if (dragging) {
      dispatch(
        setZoom({
          ...zoom,
          translate: {
            x:
              zoom.translate.x +
              e.nativeEvent.movementX / window.devicePixelRatio,
            y:
              zoom.translate.y +
              e.nativeEvent.movementY / window.devicePixelRatio,
          },
        }),
      )
    }
  }

  const onMouseLeave = () => {
    setDragging(() => false)
    dispatch(setInteractedImage(null))
    dispatch(setInert({inert: false}))
  }

  const onMouseUp = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault()
    setDragging(() => false)
    dispatch(setInteractedImage(null))
    dispatch(setInert({inert: false}))
  }

  const saveProduct = async () => {
    if (images.length > 0) {
      const image = images[0]
      let productData: SaveProductRequest
      if (twoSided && inactiveImages !== null && inactiveImages[0]) {
        const frontScale =
          activeSide === 'front' ? image.scale : inactiveImages[0].scale
        const frontPosition =
          activeSide === 'front' ? image.position : inactiveImages[0].position
        const backScale =
          activeSide === 'back' ? image.scale : inactiveImages[0].scale
        const backPosition =
          activeSide === 'back' ? image.position : inactiveImages[0].position
        productData = {
          front: {
            scale: 1 / frontScale,
            position: {
              x: 1 - frontPosition.x / frontScale,
              y: 1 - frontPosition.y / frontScale,
            },
          },
          back: {
            scale: 1 / backScale,
            position: {
              x: 1 - backPosition.x / backScale,
              y: 1 - backPosition.y / backScale,
            },
          },
        }
      } else {
        productData = {
          front: {
            scale: 1 / image.scale,
            position: {
              x: 1 - image.position.x / image.scale,
              y: 1 - image.position.y / image.scale,
            },
          },
        }
      }
      const response = await services.saveProduct(productId, productData)
      let inputField = window.parent.document.querySelector(
        '.acf-field[data-name="data"] input[type="text"]',
      )
      if (inputField) {
        ;(inputField as HTMLInputElement).value = JSON.stringify(response.data)
      }
    }
  }

  const flipSides = async () => {
    setActiveSide(oldSide => (oldSide === 'front' ? 'back' : 'front'))
  }

  useEffect(() => {
    const tempSide = [...images]
    const tempPrintArea: ImgText | undefined = printArea
      ? {...printArea}
      : undefined
    if (inactiveImages && inactivePrintArea && tempSide && tempPrintArea) {
      dispatch(flipSide({images: inactiveImages, maskImage: null}))
      dispatch(setPrintArea({printArea: inactivePrintArea}))
      setInactiveImages(tempSide)
      setInactivePrintArea(tempPrintArea)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSide])

  const selectedImage = images.find(i => i.id === selectedImageId)
  const imgToolsPosition = (): StyleSheet => {
    if (selectedImage && editorRef.current) {
      const el = document.getElementById(selectedImage.id)
      if (el) {
        const bcr = el.getBoundingClientRect()
        const bcre = editorRef.current.getBoundingClientRect()
        const left = clamp(bcr.left - bcre.left, 70, bcre.width - 70)
        const top = clamp(bcr.top - bcre.top - 40, 20, bcre.height - 30 - 20)

        return {
          left: `${left}px`,
          top: `${top}px`,
        } as StyleSheet
      }
    }
    return {}
  }

  return (
    <>
      <div className="button-container">
        <button className="editorButton" onClick={flipSides}>
          Másik oldal
        </button>

        <button type="button" className="editorButton" onClick={saveProduct}>
          Mentés
        </button>
      </div>
      <div style={styles.editor} className="editor">
        <div
          style={styles.editorColumn}
          className="editorColumn"
          ref={editorRef}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
          onMouseUp={onMouseUp}>
          <div
            style={{
              ...styles.editArea,
            }}>
            <div style={{height: '100%'}} ref={zoomRef}>
              {images.map(image => (
                <RenderImg img={image} key={`editorImageList_${image.id}`} />
              ))}
              {printArea && (
                <RenderImg
                  img={printArea}
                  key={`editorPrintArea_${printArea.id}`}
                />
              )}
            </div>
          </div>
          {selectedImage && (
            <ImgTools position={imgToolsPosition()} img={selectedImage} />
          )}
          {selectedImage &&
            selectedImage.type === 'masked' &&
            selectedImage.inside.length > 0 &&
            selectedImage.inside[0].url && (
              <InsideImgTools
                position={imgToolsPosition()}
                img={selectedImage}
                innerImageIndex={
                  interactedImage
                    ? (interactedImage.innerImageIndex as number)
                    : 0
                }
              />
            )}
        </div>
      </div>
    </>
  )
}
